// src/components/HistoricalResults.js
import React, { useEffect, useState } from 'react';
import { getCircuits } from '../utils/firestoreUtils';
import { getDocs, collection, query, where } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { Container, useTheme, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';

const formatLapTime = (time) => {
  if (!time) return '';
  const s = parseFloat(time);
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.round((s % 1) * 1000);
  if (m === 0) return `${sec.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  return `${m}:${sec.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
};

function LapTimeLink({ time, proof, style = {} }) {
  const formatted = formatLapTime(time);
  if (!formatted) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  const base = { fontFamily: 'var(--font-mono)', fontWeight: 600, ...style };
  if (proof) {
    return (
      <a href={proof} target="_blank" rel="noopener noreferrer"
        style={{ ...base, color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3 }}
        title="View proof">
        {formatted}<span style={{ fontSize: '0.7em', opacity: 0.7 }}>↗</span>
      </a>
    );
  }
  return <span style={base}>{formatted}</span>;
}

function SkeletonRows({ count = 4 }) {
  return (
    <div style={{ padding: '8px 0' }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="skeleton skeleton-row" style={{ opacity: 1 - i * 0.15, marginBottom: 2 }} />
      ))}
    </div>
  );
}

function HistoricalResults() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [circuits, setCircuits] = useState([]);
  const [circuitStandings, setCircuitStandings] = useState([]);
  const [lapRecords, setLapRecords] = useState([]);
  const [loadingStandings, setLoadingStandings] = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(true);
  // Cache all laps so year-change doesn't re-fetch Firebase
  const [allLapsCache, setAllLapsCache] = useState(null);
  const [circsCache, setCircsCache] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const circs = await getCircuits();
        setCircuits(circs);
        setCircsCache(circs);

        const usersSnapshot = await getDocs(collection(db, 'users'));
        const allLaps = [];
        const yearsSet = new Set();

        await Promise.all(usersSnapshot.docs.map(async (userDoc) => {
          const lapsSnapshot = await getDocs(query(collection(db, `users/${userDoc.id}/laps`), where('isApproved', '==', true)));
          lapsSnapshot.docs.forEach((lapDoc) => {
            const lap = { id: lapDoc.id, userId: userDoc.id, userName: userDoc.data().displayName || 'Unknown', ...lapDoc.data() };
            allLaps.push(lap);
            if (lap.date) yearsSet.add(new Date(lap.date).getFullYear());
          });
        }));

        setAllLapsCache(allLaps);
        const years = Array.from(yearsSet).sort((a, b) => b - a);
        setAvailableYears(years);
        if (!years.includes(selectedYear)) setSelectedYear(years[0] || new Date().getFullYear());

        // All-time records — include layout map
        const records = {};
        allLaps.forEach(lap => {
          const circuitId = lap.track.split('-')[0];
          if (!records[circuitId] || lap.bestLap < records[circuitId].bestLap) records[circuitId] = lap;
        });
        const recordsArray = Object.entries(records).map(([id, lap]) => {
          const circuit = circs.find(c => c.id === id);
          if (!circuit) return null;
          const layoutName = lap.track.split('-').slice(1).join('-');
          const layout = circuit.layouts?.find(l => l.layoutName === layoutName);
          return { circuit, lap, trackMap: layout?.map || null, length: layout?.length || null };
        }).filter(Boolean).sort((a, b) => a.circuit.name.localeCompare(b.circuit.name));
        setLapRecords(recordsArray);
        setLoadingRecords(false);

        buildStandings(allLaps, circs, selectedYear);
      } catch (err) {
        console.error(err);
        setLoadingStandings(false);
        setLoadingRecords(false);
      }
    };
    fetchData();
  }, []);

  const buildStandings = (allLaps, circs, year) => {
    setLoadingStandings(true);
    const yearLaps = allLaps.filter(l => l.date && new Date(l.date).getFullYear() === year);
    const standings = {};
    yearLaps.forEach(lap => {
      const circuitId = lap.track.split('-')[0];
      const layoutName = lap.track.split('-').slice(1).join('-');
      if (!standings[circuitId]) standings[circuitId] = [];
      standings[circuitId].push({ ...lap, layoutName });
    });
    const arr = Object.entries(standings).map(([id, laps]) => {
      const circuit = circs.find(c => c.id === id);
      if (!circuit) return null;
      const sorted = laps.sort((a, b) => a.bestLap - b.bestLap);
      const top3 = sorted.slice(0, 3);
      // Track map from winner's layout
      const winnerLayout = circuit.layouts?.find(l => l.layoutName === top3[0]?.layoutName);
      return { circuit, top3, trackMap: winnerLayout?.map || null };
    }).filter(Boolean);
    setCircuitStandings(arr);
    setLoadingStandings(false);
  };

  // When year changes use cache
  useEffect(() => {
    if (!allLapsCache || !circsCache) return;
    buildStandings(allLapsCache, circsCache, selectedYear);
  }, [selectedYear]);

  const podiumColors = ['var(--gold)', 'var(--silver)', 'var(--bronze)'];
  const podiumLabels = ['1ST', '2ND', '3RD'];

  return (
    <Container maxWidth="xl">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 className="page-title">Historical Results</h1>
          <p className="page-subtitle">All-time records and season standings</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Season</span>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            style={{
              padding: '8px 14px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-md)',
              background: 'var(--surface)', fontFamily: 'var(--font-body)', fontWeight: 600,
              fontSize: '0.9rem', color: 'var(--text-primary)', cursor: 'pointer', width: 'auto',
            }}
          >
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* ── TRACK RECORDS (cards with map) ── */}
      <div style={{ marginBottom: 40 }}>
        <h2 className="section-heading" style={{ marginTop: 0 }}>Track Records</h2>
        {loadingRecords ? <SkeletonRows count={5} /> : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
            {lapRecords.map((record) => (
              <div key={record.circuit.id} style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)', display: 'flex', alignItems: 'center',
                gap: 0, boxShadow: 'var(--shadow-sm)', overflow: 'hidden',
                transition: 'transform var(--transition), box-shadow var(--transition)',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
              >
                {/* Track map panel — white bg so map shows cleanly */}
                <div style={{
                  width: 80, height: 80, flexShrink: 0, background: '#ffffff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 10, borderRight: '1px solid var(--border)',
                }}>
                  {record.trackMap ? (
                    <img src={record.trackMap} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  ) : (
                    <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                      <rect x="4" y="4" width="28" height="28" rx="4" stroke="var(--border-strong)" strokeWidth="2" fill="none" strokeDasharray="4 2" />
                      <text x="18" y="22" textAnchor="middle" fontSize="10" fill="var(--text-tertiary)" fontFamily="sans-serif">—</text>
                    </svg>
                  )}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0, padding: '14px 16px' }}>
                  <Link to={`/circuit/${record.circuit.id}`} style={{
                    fontWeight: 700, fontSize: '0.875rem', color: 'var(--text-primary)',
                    textDecoration: 'none', display: 'block', whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {record.circuit.name}
                  </Link>
                  {record.length && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                      {record.length} km
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                    <LapTimeLink time={record.lap.bestLap} proof={record.lap.proof} style={{ fontSize: '1rem' }} />
                    <Link to={`/users/${record.lap.userId}`} style={{ fontSize: '0.8rem', color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>
                      {record.lap.userName}
                    </Link>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>
                      {record.lap.date ? new Date(record.lap.date).getFullYear() : ''}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── CIRCUIT STANDINGS ── */}
      <div>
        <h2 className="section-heading" style={{ marginTop: 0 }}>{selectedYear} Circuit Standings</h2>
        {loadingStandings ? <SkeletonRows count={4} /> : circuitStandings.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-tertiary)' }}>
            No results for {selectedYear} yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {circuitStandings.map((standing) => (
              <div key={standing.circuit.id} style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)', overflow: 'hidden', boxShadow: 'var(--shadow-sm)',
              }}>
                {/* Circuit header */}
                <div style={{
                  padding: '10px 16px', background: 'var(--surface-2)',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  {/* Track map thumbnail */}
                  {standing.trackMap && (
                    <div style={{
                      width: 32, height: 32, flexShrink: 0, background: '#ffffff',
                      borderRadius: 5, padding: 3, border: '1px solid var(--border)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <img src={standing.trackMap} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </div>
                  )}
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
                  <Link to={`/circuit/${standing.circuit.id}`} style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)', textDecoration: 'none' }}>
                    {standing.circuit.name}
                  </Link>
                </div>
                {/* Podium finishers */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)' }}>
                  {[0, 1, 2].map((idx) => {
                    const finisher = standing.top3[idx];
                    return (
                      <div key={idx} style={{
                        padding: '14px 20px',
                        borderRight: !isMobile && idx < 2 ? '1px solid var(--border)' : 'none',
                        borderTop: isMobile && idx > 0 ? '1px solid var(--border)' : 'none',
                        display: 'flex', alignItems: 'center', gap: 12,
                      }}>
                        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: podiumColors[idx], lineHeight: 1, flexShrink: 0, width: 36, textAlign: 'center' }}>
                          {podiumLabels[idx]}
                        </div>
                        {finisher ? (
                          <div>
                            <Link to={`/users/${finisher.userId}`} style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-primary)', textDecoration: 'none' }}>
                              {finisher.userName}
                            </Link>
                            <div style={{ marginTop: 3 }}>
                              <LapTimeLink time={finisher.bestLap} proof={finisher.proof} style={{ fontSize: '0.82rem' }} />
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>—</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Container>
  );
}

export default HistoricalResults;
